import { ListModal, ListStep, useTokens } from '@reservoir0x/reservoir-kit-ui'
import { Button } from 'components/primitives'
import {
  cloneElement,
  ComponentProps,
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  useContext,
  useState,
} from 'react'
import { CSS } from '@stitches/react'
import { SWRResponse } from 'swr'
import { ToastContext } from 'context/ToastContextProvider'
import { useMarketplaceChain } from 'hooks'
import { useAuthWallets } from 'hooks/auth/useAuthWallets'
import { useAuthLogin } from 'hooks/auth/useAuthLogin'

import {
  ClickAnalyticsProps,
  withClickAnalytics,
} from 'components/wrappers/withClickAnalytics'
import { analytics } from 'utils/helpers/analytics'
import { useAccountPrivyWallet } from 'hooks/auth/useAccountPrivyWallet'
import { getReservoirChainsFromContract } from 'config/routes'
import useNFTChain from 'hooks/useNFTChain'

const conduitKey = process.env.NEXT_PUBLIC_CONDUIT_KEY
const orderFee = process.env.NEXT_PUBLIC_MARKETPLACE_FEE

type ListingCurrencies = ComponentPropsWithoutRef<
  typeof ListModal
>['currencies']

type Props = {
  analyticsId?: string
  token?: ReturnType<typeof useTokens>['data'][0]
  buttonCss?: CSS
  buttonChildren?: ReactNode
  buttonProps?: ComponentProps<typeof Button>
  mutate?: SWRResponse['mutate']
  isOpen?: boolean
  closeOnComplete?: boolean
}

const List: FC<Props & ClickAnalyticsProps> = withClickAnalytics(
  ({
    analyticsId,
    token,
    buttonCss,
    buttonChildren,
    buttonProps,
    mutate,
    isOpen = false,
    closeOnComplete = false,
  }) => {
    const { ownershipWallet } = useAuthWallets()
    const { changeAccountActiveWallet } = useAccountPrivyWallet()
    const isDisconnected = Boolean(!ownershipWallet?.address)
    const { login } = useAuthLogin()
    const { addToast } = useContext(ToastContext)
    const [isModalOpen, setIsModalOpen] = useState(isOpen)

    const marketplaceChain = useMarketplaceChain()

    const listingCurrencies: ListingCurrencies =
      marketplaceChain.listingCurrencies
    const tokenId = token?.token?.tokenId
    const collectionId = token?.token?.collection?.id
    const { DefaultChain } = useNFTChain()
    const chain = getReservoirChainsFromContract(collectionId ?? '')

    const trigger = (
      <Button
        css={buttonCss}
        color="primary"
        {...buttonProps}
        onClick={() => {
          changeAccountActiveWallet()
        }}
      >
        {buttonChildren}
      </Button>
    )

    const orderFees = orderFee ? [orderFee] : []

    if (isDisconnected) {
      return cloneElement(trigger, {
        onClick: async () => {
          if (!ownershipWallet?.address) {
            login?.()
          }
        },
      })
    } else
      return (
        <ListModal
          openState={[isModalOpen, setIsModalOpen]}
          trigger={trigger}
          collectionId={collectionId}
          // conduitKey={conduitKey}
          tokenId={tokenId}
          feesBps={orderFees}
          currencies={listingCurrencies}
          chainId={chain[0]?.id ?? DefaultChain.id}
          onClose={(data, stepData, currentStep) => {
            if (mutate && currentStep == ListStep.Complete) mutate()
            if (analyticsId) {
              analytics.trackWebEvent(analyticsId).catch(console.error)
            }
            if (closeOnComplete) {
              window.close()
            }
          }}
          // orderKind="seaport-v1.5"
          onListingError={(err: any) => {
            // console.log()
            if (err?.code === 4001) {
              addToast?.({
                title: 'User canceled transaction',
                description: 'You have canceled the transaction.',
              })
              return
            }
            console.log('onListingError err', err)
            addToast?.({
              title: 'Could not list token',
              description: 'The transaction was not completed.',
            })
          }}
          onPointerDownOutside={(e) => {
            const privyLayer = document.getElementById('privy-dialog')

            const clickedInsidePrivyLayer =
              privyLayer && e.target
                ? privyLayer.contains(e.target as Node)
                : false

            if (clickedInsidePrivyLayer) {
              e.preventDefault()
            }
          }}
        />
      )
  }
)
export default List
